import {
  Paper,
  Modal,
  Button,
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import React, { useState, useRef, useEffect, useMemo } from "react";
import axios from "axios";

/** Display width in mm with three decimal places. */
function formatWidthMm(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return `${n.toFixed(3)} mm`;
}

/** Width for modal badge; treat -1 as not yet measured. */
function formatModalWidthMm(value) {
  if (value === -1 || value == null || value === "") return "—";
  return formatWidthMm(value);
}

/** Badge: classification text for Unmarked/Burnt; width (mm) when Marked. */
function getPatchBadgeLabel(image) {
  const pt = image.patch_type;
  if (pt === "Unmarked") return "Unmarked";
  if (pt === "Burnt") return "Burnt";
  if (pt === "Marked") return formatWidthMm(image.cluster_width);
  return formatWidthMm(image.cluster_width);
}

function getPatchBadgeTooltip(image) {
  const pt = image.patch_type;
  if (pt === "Unmarked") return "Unmarked";
  if (pt === "Burnt") return "Burnt";
  return "Width (mm)";
}

/**
 * The width review grid of one iteration's cluster patches. ``explorationId``
 * names the exploration the measurements belong to and ``batchNumber`` the
 * patch group a patch without a batch of its own is stored under.
 */
const ImageGrid = ({ imageObjects, gridSize, updateGridCellContent, explorationId, batchNumber }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [coordinates, setCoordinates] = useState([]);
  const [row, setRow] = useState(null);
  const [column, setColumn] = useState(null);
  const [width_pixel, setWidth_pixel] = useState(null);
  const [method, setMethod] = useState(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [selectedValue, setSelectedValue] = useState("");
  const [widthValue, setWidthValue] = useState(-1);
  const [calibrationValue, setCalibrationValue] = useState(1);
  const [points, setPoints] = useState([]);
  // Whether the user touched the edges in this modal session (placed a point
  // or pressed "Clear edges"). The canvas always opens with an empty
  // `points` list, so without this flag a plain Save could not be told apart
  // from a deliberate clear and would wipe the stored edges of every patch
  // the user only looked at.
  const [edgesEdited, setEdgesEdited] = useState(false);

  const canvasRef = useRef(null);
  const gridCanvasRefs = useRef([]);
  const imageRef = useRef(null);

  const handleChange = async (
    image,
    index,
    coordinates,
    cluster_width,
    row,
    column,
    width_pixel,
    width_metric,
    method,
    patch_type,
    batch_number
  ) => {
    updateGridCellContent(
      coordinates,
      cluster_width,
      row,
      column,
      width_pixel,
      cluster_width,
      method,
      patch_type,
      index
    );

    const request_data = {
      explorationId,
      edge_a_location: coordinates[0],
      edge_b_location: coordinates[1],
      cluster_width,
      row,
      column,
      width_pixel,
      width_metric,
      method,
      patch_classification: patch_type,
      batch_number:
        batch_number !== undefined && batch_number !== null
          ? batch_number
          : batchNumber,
    };

    await axios.post("api/update/width/measurement", request_data).catch((error) => {
      console.error("Error updating measurement:", error);
    });
  };

  const handleImageClickGrid = (
    image,
    index,
    coordinates,
    cluster_width,
    row,
    column,
    width_pixel,
    width_metric,
    method,
    patch_type,
    batch_number
  ) => {
    setSelectedImage(image);
    setSelectedImageIndex(index);
    setSelectedBatch(batch_number);
    setModalOpen(true);
    setCoordinates(coordinates || []);
    setRow(row);
    setColumn(column);
    setWidth_pixel(width_pixel);
    setMethod(method);
    setPoints([]);
    setEdgesEdited(false);
    setSelectedValue(patch_type);
    setWidthValue(width_metric || -1);
    const img = new Image();
    img.src = image;
    img.onload = () => {
      const mm_per_pixel = 1.0 / img.naturalHeight;
      setCalibrationValue(mm_per_pixel);
    };
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedImage(null);
    setSelectedImageIndex(null);
    setSelectedBatch(null);
    setPoints([]);
    setEdgesEdited(false);
  };

  const drawRedLines = (ctx, coordinates, imageWidth, imageHeight) => {
    if (coordinates && coordinates.length === 2) {
      const [y1, y2] = coordinates.map(
        (coord) => (coord * imageHeight) / imageHeight
      );

      ctx.beginPath();
      ctx.moveTo(0, y1);
      ctx.lineTo(imageWidth, y1);
      ctx.moveTo(0, y2);
      ctx.lineTo(imageWidth, y2);
      ctx.lineWidth = 2;
      ctx.strokeStyle = "red";
      ctx.stroke();
    }
  };

  const prepareGridCanvas = (canvas, image, coordinates) => {
    if (!canvas || !image) return;

    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.src = image;

    img.onload = () => {
      const imageWidth = img.naturalWidth;
      const imageHeight = img.naturalHeight;

      canvas.width = imageWidth;
      canvas.height = imageHeight;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      drawRedLines(ctx, coordinates, imageWidth, imageHeight);
    };
  };

  const handleImageClick = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;

    const xPixel = x * imageSize.width;
    const yPixel = y * imageSize.height;
    if (points.length === 2) {
      return;
    }
    setEdgesEdited(true);
    setPoints((prev) => {
      const updatedPoints = [...prev, { xPixel, yPixel }];
      if (updatedPoints.length === 2) calculateWidth(updatedPoints);
      drawLines(updatedPoints);
      return updatedPoints.slice(0, 2);
    });
  };

  const drawLines = (updatedPoints) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(imageRef.current, 0, 0, canvas.width, canvas.height);

    updatedPoints.forEach((point) => {
      ctx.beginPath();
      ctx.moveTo(0, point.yPixel);
      ctx.lineTo(canvas.width, point.yPixel);
      ctx.lineWidth = 2;
      ctx.strokeStyle = "red";
      ctx.stroke();
    });
  };

  const handleClearCornerPoints = () => {
    setPoints([]);
    setEdgesEdited(true);
    setWidthValue(-1);
    setWidth_pixel(-1);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(imageRef.current, 0, 0, canvas.width, canvas.height);
  };

  const calculateWidth = (points) => {
    const [point1, point2] = points;
    const widthPixel = Math.abs(point2.yPixel - point1.yPixel);
    const calibratedWidth = Math.round(widthPixel) * calibrationValue;
    setWidthValue(calibratedWidth);
    setWidth_pixel(widthPixel);
  };

  const saveChangesClick = async () => {
    // Three distinct cases, only the first of which is an actual re-measure:
    //   both edges placed  -> store them and the width derived from them
    //   "Clear edges"      -> store the -1 sentinels (no edges, no width)
    //   nothing touched    -> keep the stored edges/width exactly as they are
    // The last case used to fall through to the -1 sentinels, which erased
    // the edges of every patch the user opened and closed with Save.
    const hasNewEdges = points.length === 2;
    let nextCoordinates;
    if (hasNewEdges) {
      nextCoordinates = points.map((p) => p.yPixel);
    } else if (edgesEdited) {
      nextCoordinates = [];
    } else {
      nextCoordinates = coordinates && coordinates.length === 2 ? coordinates : [];
    }
    // -1 is the sentinel the width quantification itself uses for "no edge".
    const hasEdges =
      nextCoordinates.length === 2 &&
      nextCoordinates[0] >= 0 &&
      nextCoordinates[1] >= 0;
    const edgeA = hasEdges ? nextCoordinates[0] : -1;
    const edgeB = hasEdges ? nextCoordinates[1] : -1;
    // Same convention as width_calculation.py: no edges means no width.
    const updatedWidthMetric = hasEdges ? widthValue : -1;
    const updatedWidthPixel = hasEdges ? width_pixel : -1;
    // The edges ARE the classification: placing two by hand says the patch is
    // measurable, leaving none says it is not. Burnt is a separate, explicit
    // judgement that also carries no edges, so it is never overwritten here.
    let nextClassification = selectedValue;
    if (hasEdges) {
      nextClassification = "Marked";
    } else if (selectedValue !== "Burnt") {
      nextClassification = "Unmarked";
    }

    const request_data = {
      explorationId,
      edge_a_location: edgeA,
      edge_b_location: edgeB,
      cluster_width: updatedWidthMetric,
      row,
      column,
      width_pixel: updatedWidthPixel,
      width_metric: updatedWidthMetric,
      method,
      patch_classification: nextClassification,
      batch_number:
        selectedBatch !== undefined && selectedBatch !== null
          ? selectedBatch
          : batchNumber,
    };
    await axios.post("api/update/width/measurement", request_data);
    setSelectedValue(nextClassification);
    updateGridCellContent(
      hasEdges ? nextCoordinates : [],
      updatedWidthMetric,
      row,
      column,
      updatedWidthPixel,
      updatedWidthMetric,
      method,
      nextClassification,
      selectedImageIndex
    );
    handleCloseModal();
  };

  useEffect(() => {
    if (selectedImage) {
      const img = new Image();
      img.src = selectedImage;
      img.onload = () => {
        // The modal can be closed before the patch image finishes decoding.
        const canvas = canvasRef.current;
        if (!canvas) return;
        setImageSize({ width: img.naturalWidth, height: img.naturalHeight });

        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;

        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        drawRedLines(ctx, coordinates, img.naturalWidth, img.naturalHeight);
        imageRef.current = img;
      };
    }
    // Load all images immediately when the component mounts
    imageObjects.forEach((image, index) => {
      const canvas = gridCanvasRefs.current[index];
      prepareGridCanvas(canvas, image.image, image.coordinates);
    });
  }, [imageObjects, selectedImage]);

  // Group cells by batch_number while preserving the original index for state-tracking.
  // When no batch_number is present (single-batch flow), everything lands in a single group.
  const groupedByBatch = useMemo(() => {
    const groups = new Map();
    imageObjects.forEach((image, originalIndex) => {
      const key =
        image && image.batch_number !== undefined && image.batch_number !== null
          ? image.batch_number
          : "__default__";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push({ image, originalIndex });
    });
    return Array.from(groups.entries())
      .map(([batchKey, items]) => ({ batchKey, items }))
      .sort((a, b) => {
        if (a.batchKey === "__default__") return -1;
        if (b.batchKey === "__default__") return 1;
        return Number(a.batchKey) - Number(b.batchKey);
      });
  }, [imageObjects]);

  const showBatchHeaders =
    groupedByBatch.length > 1 ||
    (groupedByBatch.length === 1 && groupedByBatch[0].batchKey !== "__default__");

  const renderCell = (image, originalIndex) => (
    <Box
      key={originalIndex}
      sx={{
        p: 2,
        minWidth: 0,
        borderRadius: 2,
        bgcolor: "grey.50",
        border: "solid",
        borderWidth: selectedImageIndex === originalIndex && modalOpen ? 2 : 1,
        borderColor:
          selectedImageIndex === originalIndex && modalOpen
            ? "primary.main"
            : "rgba(0, 0, 0, 0.06)",
        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        "&:hover": {
          borderColor:
            selectedImageIndex === originalIndex && modalOpen
              ? "primary.main"
              : "rgba(0, 0, 0, 0.12)",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
        },
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        aria-label="Edit width borders"
        onClick={() =>
          handleImageClickGrid(
            image.image,
            originalIndex,
            image.coordinates,
            image.cluster_width,
            image.row,
            image.column,
            image.width_pixel,
            image.width_metric,
            image.method,
            image.patch_type,
            image.batch_number
          )
        }
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleImageClickGrid(
              image.image,
              originalIndex,
              image.coordinates,
              image.cluster_width,
              image.row,
              image.column,
              image.width_pixel,
              image.width_metric,
              image.method,
              image.patch_type,
              image.batch_number
            );
          }
        }}
        sx={{
          width: "100%",
          minHeight: 160,
          borderRadius: 1,
          overflow: "hidden",
          bgcolor: "common.white",
          lineHeight: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          userSelect: "none",
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: 2,
          },
        }}
      >
        <canvas
          ref={(ref) => {
            if (ref) {
              gridCanvasRefs.current[originalIndex] = ref;
            }
          }}
          style={{
            width: "100%",
            maxWidth: "100%",
            height: "auto",
            maxHeight: 220,
            display: "block",
            objectFit: "contain",
            pointerEvents: "none",
          }}
        />
      </Box>

      <Tooltip title={getPatchBadgeTooltip(image)}>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
          <Chip
            size="small"
            variant="outlined"
            label={getPatchBadgeLabel(image)}
            sx={{
              fontWeight: 600,
              color: "text.secondary",
              borderColor: "divider",
              bgcolor: "background.paper",
              maxWidth: "100%",
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            }}
          />
        </Box>
      </Tooltip>

      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={0.5}
        sx={{ mt: 1.25 }}
      >
        <Tooltip title="Unmarked">
          <IconButton
            size="small"
            aria-label="Classify as unmarked"
            color={image.patch_type === "Unmarked" ? "primary" : "default"}
            onClick={() =>
              handleChange(
                image.image,
                originalIndex,
                image.coordinates,
                image.cluster_width,
                image.row,
                image.column,
                image.width_pixel,
                image.width_metric,
                image.method,
                "Unmarked",
                image.batch_number
              )
            }
          >
            <RadioButtonUncheckedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Marked">
          <IconButton
            size="small"
            aria-label="Classify as marked"
            color={image.patch_type === "Marked" ? "primary" : "default"}
            onClick={() =>
              handleChange(
                image.image,
                originalIndex,
                image.coordinates,
                image.cluster_width,
                image.row,
                image.column,
                image.width_pixel,
                image.width_metric,
                image.method,
                "Marked",
                image.batch_number
              )
            }
          >
            <CheckCircleIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Burnt">
          <IconButton
            size="small"
            aria-label="Classify as burnt"
            color={image.patch_type === "Burnt" ? "primary" : "default"}
            onClick={() =>
              handleChange(
                image.image,
                originalIndex,
                image.coordinates,
                image.cluster_width,
                image.row,
                image.column,
                image.width_pixel,
                image.width_metric,
                image.method,
                "Burnt",
                image.batch_number
              )
            }
          >
            <LocalFireDepartmentIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );

  return (
    <>
      <Stack spacing={3} sx={{ width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
        {groupedByBatch.map(({ batchKey, items }) => (
          <Box key={String(batchKey)} sx={{ width: "100%", minWidth: 0 }}>
            {showBatchHeaders ? (
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1.5,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  fontWeight: 600,
                }}
              >
                {batchKey === "__default__"
                  ? "Patch group"
                  : `Patch group ${Number(batchKey) + 1}`}
              </Typography>
            ) : null}
            <Box
              sx={{
                width: "100%",
                maxWidth: "100%",
                boxSizing: "border-box",
                display: "grid",
                gap: 2.5,
                gridTemplateColumns: {
                  xs: "repeat(1, minmax(0, 1fr))",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(4, minmax(0, 1fr))",
                },
              }}
            >
              {items.map(({ image, originalIndex }) => renderCell(image, originalIndex))}
            </Box>
          </Box>
        ))}
      </Stack>

      <Modal open={modalOpen} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            outline: "none",
            maxHeight: "90vh",
            overflow: "auto",
            width: "min(calc(100% - 32px), 600px)",
          }}
        >
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            p: { xs: 2, sm: 2.5 },
            width: "100%",
            borderRadius: 2,
            borderColor: "divider",
            boxSizing: "border-box",
          }}
        >
          <canvas
            ref={canvasRef}
            style={{
              width: "100%",
              height: "100%",
              cursor: "crosshair",
              border: "1px solid rgba(0, 0, 0, 0.23)",
              borderRadius: 4,
              display: "block",
              verticalAlign: "top",
            }}
            onClick={handleImageClick}
          />
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Tooltip title="Width (mm)">
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Chip
                  size="small"
                  variant="outlined"
                  label={formatModalWidthMm(widthValue)}
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                />
              </Box>
            </Tooltip>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.25}
              justifyContent="center"
              alignItems="center"
            >
              <Button
                variant="outlined"
                onClick={handleClearCornerPoints}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Clear edges
              </Button>
              <Tooltip title={points.length === 1 ? "Select the second edge" : ""}>
                <span>
                  <Button
                    variant="contained"
                    onClick={saveChangesClick}
                    disabled={points.length === 1}
                    sx={{ textTransform: "none", fontWeight: 600 }}
                  >
                    Save
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </Paper>
        </Box>
      </Modal>
    </>
  );
};

export default ImageGrid;
